import React, { useState } from 'react';
import { Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ApprovalFieldDef, ApprovalTermFieldStatus } from 'modules/creditSubmission/submissionDetails/types';

export const APPROVAL_FIELDS: ApprovalFieldDef[] = [
  { key: 'lenderName', label: 'Lender Name', type: 'text' },
  { key: 'decision', label: 'Decision', type: 'text' },
  { key: 'applicantName', label: 'Applicant Name', type: 'text' },
  { key: 'approvalAmount', label: 'Approved Amount', type: 'currency' },
  { key: 'termLengthMonths', label: 'Term (Months)', type: 'number' },
  { key: 'monthlyPayment', label: 'Monthly Payment', type: 'currency' },
  { key: 'interestRate', label: 'Interest Rate', type: 'percentage' },
  { key: 'downPaymentToVendor', label: 'Down Payment', type: 'currency' },
  { key: 'docFees', label: 'Doc Fees', type: 'currency' },
  { key: 'minimumRequiredDownPayment', label: 'Minimum Required Down Payment', type: 'currency' },
  { key: 'daysToFirstPayment', label: 'Days To First Payment', type: 'text' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusIcon: React.FC<{ status: ApprovalTermFieldStatus }> = ({ status }) => {
  if (status === 'extracted') return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
  if (status === 'edited') return <EditIcon sx={{ fontSize: 18, color: 'primary.main' }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
};

interface EditableFieldRowProps {
  label: string;
  value: string;
  displayValue: string;
  status: ApprovalTermFieldStatus;
  onChange: (val: string) => void;
  onSave: () => void;
  type?: 'text' | 'number';
  isDecisionField?: boolean;
}

interface ApprovalDetailsPanelProps {
  form: Record<string, string>;
  originalValues: Record<string, string>;
  getFieldStatus: (key: string) => ApprovalTermFieldStatus;
  getDisplayValue: (key: string, fDef: ApprovalFieldDef) => string;
  onFieldChange: (key: string) => (val: string) => void;
  onSave: () => void;
}

const EditableFieldRow: React.FC<EditableFieldRowProps> = ({
  label,
  value,
  displayValue,
  status,
  onChange,
  onSave,
  type = 'text',
  isDecisionField,
}) => {
  const [editing, setEditing] = useState(false);

  const handleSave = () => {
    setEditing(false);
    onSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <StatusIcon status={status} />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {isDecisionField && !editing ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={displayValue || 'Unknown'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ) : editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              type={type}
              autoFocus
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderColor: 'primary.main' },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              sx={{ minWidth: 60, textTransform: 'uppercase', fontWeight: 600 }}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Box
            onClick={() => setEditing(true)}
            sx={{
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" color={status === 'empty' ? 'text.disabled' : 'text.primary'}>
              {status === 'empty' ? 'Not extracted — click to add' : displayValue}
            </Typography>
            {status === 'edited' && (
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                Edited
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ApprovalDetailsPanel: React.FC<ApprovalDetailsPanelProps> = ({
  form,
  originalValues,
  getFieldStatus,
  getDisplayValue,
  onFieldChange,
  onSave,
}) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={700}>
        Approval Details
      </Typography>
    </Box>
    {APPROVAL_FIELDS.map(fDef => {
      const status = getFieldStatus(fDef.key);
      return (
        <EditableFieldRow
          key={fDef.key}
          label={fDef.label}
          value={form[fDef.key] ?? ''}
          displayValue={getDisplayValue(fDef.key, fDef)}
          status={status}
          onChange={onFieldChange(fDef.key)}
          onSave={onSave}
          type={fDef.type === 'currency' || fDef.type === 'number' || fDef.type === 'percentage' ? 'number' : 'text'}
          isDecisionField={fDef.key === 'decision'}
        />
      );
    })}
  </Paper>
);
